// ─────────────────────────────────────────────────────────────
// Vendor-agnostic analytics loader
//
// Reads `process.env.GATSBY_ANALYTICS_VENDOR` ('plausible' | 'ga4'
// | unset). Loads the appropriate script tag IFF the user has
// granted consent for 'all' (see cookie-consent.tsx).
//
// Listens for the `ctrls:consent-changed` event so a user who
// flips from "necessary" → "all" mid-session gets analytics
// initialized without a reload.
//
// Idempotent: tracks loaded state so flipping consent back and
// forth doesn't double-inject scripts.
// ─────────────────────────────────────────────────────────────

import { getConsent } from '@/components/elements/cookie-consent'

type Vendor = 'plausible' | 'ga4' | 'none'

let loaded = false

function getVendor(): Vendor {
  const v = (process.env.GATSBY_ANALYTICS_VENDOR || '').toLowerCase()
  if (v === 'plausible') return 'plausible'
  if (v === 'ga4') return 'ga4'
  return 'none'
}

function injectPlausible() {
  // Plausible — privacy-first, EU-hosted, cookie-less by default.
  // Domain is taken from window.location.host so a single env var
  // works across staging/prod.
  const s = document.createElement('script')
  s.defer = true
  s.dataset.domain = window.location.host
  s.src = 'https://plausible.io/js/script.js'
  document.head.appendChild(s)
}

function injectGa4() {
  const id = process.env.GATSBY_GA4_MEASUREMENT_ID
  if (!id) {
    // eslint-disable-next-line no-console
    console.warn('[analytics] GATSBY_ANALYTICS_VENDOR=ga4 but GATSBY_GA4_MEASUREMENT_ID unset; skipping.')
    return
  }
  const s1 = document.createElement('script')
  s1.async = true
  s1.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`
  document.head.appendChild(s1)
  const s2 = document.createElement('script')
  s2.text = `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', '${id}', { anonymize_ip: true });`
  document.head.appendChild(s2)
}

function load() {
  if (loaded) return
  if (getConsent() !== 'all') return
  const vendor = getVendor()
  if (vendor === 'none') return
  if (vendor === 'plausible') injectPlausible()
  if (vendor === 'ga4') injectGa4()
  loaded = true
}

export function initAnalytics() {
  if (typeof window === 'undefined') return
  // Try once on mount.
  load()
  // Re-try when the user changes consent.
  window.addEventListener('ctrls:consent-changed', load as EventListener)
}
